import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <nav className="border-b border-[var(--border)] bg-white/70 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[900px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-brand">
            <div className="w-8 h-8 rounded-md bg-[var(--fg)] text-white grid place-items-center font-bold">IF</div>
            <div className="text-[15px] font-semibold tracking-tight">InvoiceForge</div>
            <span className="text-xs text-gray-400 ml-2">by PrismGrim</span>
          </Link>
          <Link to="/" className="text-sm px-3 py-2 hover:bg-gray-100 rounded-md inline-flex items-center gap-2" data-testid="terms-back">
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
        </div>
      </nav>

      <div className="max-w-[900px] mx-auto px-6 py-16" data-testid="page-terms">
        <div className="overline mb-3">Legal</div>
        <h1 className="text-5xl leading-none tracking-tight font-medium mb-8">Terms &amp; Conditions</h1>
        <p className="text-sm text-gray-500 mb-10 font-mono">Effective from 18 Feb 2026 · v1.0</p>

        <div className="prose prose-neutral max-w-none space-y-10">
          <section>
            <h2 className="text-2xl font-medium tracking-tight mb-3">1. Acceptance</h2>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              By creating an account or using InvoiceForge (operated by <strong>PrismGrim</strong>), you accept these
              Terms &amp; Conditions in full. If you do not accept these terms, please do not register or use the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight mb-3">2. Cookies &amp; Cache</h2>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              We use HttpOnly authentication cookies to keep you signed in, and standard browser cache to speed up
              the site. We do <strong>not</strong> use third-party advertising or tracking cookies. By clicking
              &ldquo;Accept&rdquo; on the cookie banner or by registering, you consent to these essential cookies and
              local browser storage.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight mb-3">3. Data Visibility &amp; Modification Permissions</h2>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              When you register you may grant, or revoke at any time, the following permissions to PrismGrim
              administrators:
            </p>
            <ul className="list-disc list-inside text-[15px] text-gray-700 leading-relaxed mt-3 space-y-2 pl-2">
              <li>
                <strong>Data-visibility permission</strong> — allows a PrismGrim administrator to view your account
                data (invoices, receipts, clients, company profile) for support &amp; audit purposes.
              </li>
              <li>
                <strong>Data-modification permission</strong> — allows a PrismGrim administrator to update your
                profile (for example, correcting your name or company details) on your request.
              </li>
            </ul>
            <p className="text-[15px] text-gray-700 leading-relaxed mt-3">
              Both permissions default to <strong>OFF</strong>. You may toggle them at any time from
              <em> Company Profile → Privacy</em>. Administrators cannot see or modify your data unless you have
              explicitly granted the corresponding permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight mb-3">4. Your Data</h2>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              You retain full ownership of all invoices, receipts, client records and company data you create.
              We store this data solely to provide the service to you. You can request an export or full deletion
              of your data at any time by contacting support.
            </p>
          </section>

          <section className="rounded-lg border border-red-200 bg-red-50/60 p-6" data-testid="terms-liability">
            <h2 className="text-2xl font-medium tracking-tight mb-3 text-red-900">5. Limitation of Liability</h2>
            <p className="text-[15px] text-red-950 leading-relaxed">
              <strong>InvoiceForge / PrismGrim is NOT responsible for any hacking, unauthorised access, data
              breach, data leak, ransomware, phishing, denial-of-service attack, third-party compromise, or any
              related security incident</strong> that may affect the platform or your data. You use the service
              &ldquo;as is&rdquo; and at your own risk. To the maximum extent permitted by applicable law,
              PrismGrim disclaims all liability for direct, indirect, incidental, consequential, or punitive
              damages arising out of, or in connection with, your use of the service — including but not limited
              to loss of revenue, loss of data, loss of profits, or business interruption.
            </p>
            <p className="text-[15px] text-red-950 leading-relaxed mt-3">
              You are solely responsible for keeping your password strong and confidential and for the accuracy
              of all information you enter into the platform, including tax numbers (GSTIN/PAN), addresses, and
              banking details on generated documents.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight mb-3">6. Acceptable Use</h2>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              You agree not to use InvoiceForge for any illegal purpose, to issue fraudulent invoices, to evade
              tax obligations, or to abuse the platform (spam, automated scraping, attempting to break security).
              We may suspend or terminate accounts that violate these terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight mb-3">7. Changes to these Terms</h2>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              We may update these Terms from time to time. Material changes will be announced in-product. Continued
              use of the service after changes are posted constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-medium tracking-tight mb-3">8. Contact</h2>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              Questions? Contact PrismGrim at <span className="font-mono">support@prismgrim.io</span>.
            </p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-[var(--border)] text-xs text-gray-500 font-mono flex items-center justify-between">
          <span>© 2026 PrismGrim · InvoiceForge</span>
          <span>v1.0 · 18-Feb-2026</span>
        </div>
      </div>
    </div>
  );
}
